import errorHandlers from '@/services/error-handlers';
import events from '@/services/events';

import http from '@/services/http';
import applicationFlows from '@/constants/applicationFlows';

import moment from 'moment-timezone';
import configureProfileInfo from '@/helpers/configure-profile-info';
import storage from '@/services/storage';
import pickupTimes from '@/services/pickup-times';
import { v4 as uuid } from 'uuid';

const formatAuthVerificationBody = ({ contactInfo, contactType, ...rest }) => {
  const infoData = contactType === 'email'
    ? { email: contactInfo }
    : { phoneNumber: contactInfo.replace(/[^0-9]/g, '') };

  return {
    ...infoData,
    ...rest
  };
};


export default {
  namespaced: true,

  state: {
    applicationFlow: null,
    campaignId: null,
    currentUser: {
      isLoading: false,
      isRefreshing: false,
      accountId: null,
      accountStatus: null,
      allowInStoreOfferRedemption: false,
      allowOfferRedemption: false,
      birthdayInfo: null,
      company: null,
      email: null,
      emailVerified: false,
      firstName: null,
      isEnrolledInLoyalty: false,
      lastName: null,
      notificationPreferences: {
        email: false,
        marketingOptIn: false,
        sms: false
      },
      primarySmsNumber: null,
      primarySmsNumberVerified: false,
      usesThirdPartyAuthentication: false,
      zip: null,
      favoriteStore: {
        businessLocationId: null,
        description: null,
        isFavorite: null,
        storeId: null
      },
      houseAccounts: [],
      unreadInboxMessageCount: 0
    },
    emailForReceipt: null,
    initialPageLoad: true,
    isPortalAuthSession: false,
    isSignedIn: false,
    hasBeenPromptedToEnrollInLoyalty: false,
    hasVerifiedAgeForAlcohol: false,
    locationId: null,
    orderDetails: {
      deliveryAddress: null, // An object that will contain delivery address details
      deliveryEstimate: null, // An object that will contain delivery estimate details (fee, estimated time, etc)
      deliveryAddressGeoCoordinates: { latitude: null, longitude: null },
      expirationTime: null, // An optional timestamp that'll be used to expire the order session at a set time
      fulfillmentTime: null, // A timestamp that determines pickup or delivery time
      fulfillmentType: null, // "pick-up", "delivery", "dine-in", "room-service", "shipping", or "inherit" (for inheriting from host's group order)
      pickupType: null, // "take-out" | "curbside" | "drive-through"
      groupMemberGuestId: null, // For group orders, keep track of their guest ID
      menuType: null, // An optional, override menu type (usually should be null) - used when a fulfillment type can have multiple menus.
      orderType: null, // "standard" = standard order ahead, "catering" = catering order ahead, "group" = group order ahead, "table" = order at table, "pay" = pay at table
      tableNumber: null
    },
    orderExpirationTimer: null,
    orderId: null,
    prefillAccountInfo: null,
    signInRedirectRoute: null,
    route: {
      action: null,
      cancelUrl: null,
      channelTypeId: null,
      customQuery: null,
      disableSplit: false,
      enrollInLoyalty: false,
      merchantId: null,
      merchantLocationId: null,
      orderId: null,
      orderGuid: null,
      orderType: null,
      partnerIdentification: null,
      posLocationId: null,
      returnUrl: null,
      storeId: null,
      sublocationToken: null,
      successUrl: null,
      tableNumber: null,
      ticketNumber: null,
      userLocationType: null,
      utmSource: null,
      isTableTopDevice: false
    },
    smsNumberForReceipt: null,
    smsReceiptOptIn: false,
    websocketUser: {
      id: null,
      nickname: null
    }
  },

  getters: {
    campaignId(state) {
      return state.campaignId;
    },

    currentUser(state) {
      let dateOfBirth;
      let fullName;
      const { firstName, lastName } = state.currentUser;

      if (state.currentUser.birthdayInfo) {
        const { day, month, year } = state.currentUser.birthdayInfo;
        const dayFormatted = day.toString().length > 1 ? day : `0${day}`;
        const monthFormatted = month.toString().length > 1 ? month : `0${month}`;
        dateOfBirth = `${monthFormatted}/${dayFormatted}`;

        if (year) {
          dateOfBirth = `${dateOfBirth}/${year}`;
        }
      }

      if (firstName && lastName) {
        fullName = `${firstName} ${lastName}`;
      }

      const accountVerificationStatus = {
        isEmailRequired: state.currentUser.emailVerificationRequired,
        isPhoneRequired: state.currentUser.phoneNumberVerificationRequired,
        isVerificationRequired: state.currentUser.accountId && state.currentUser.accountStatus !== 'Active'
      };

      return {
        ...state.currentUser,
        isSignedIn: state.isSignedIn,
        accountVerificationStatus,
        dateOfBirth,
        fullName
      };
    },

    applicationFlow(state) {
      return state.applicationFlow;
    },

    orderDetails(state) {
      return state.orderDetails;
    },

    session(state) {
      return {
        applicationFlow: state.applicationFlow,
        currentUser: state.currentUser,
        emailForReceipt: state.emailForReceipt,
        hasBeenPromptedToEnrollInLoyalty: state.hasBeenPromptedToEnrollInLoyalty,
        hasVerifiedAgeForAlcohol: state.hasVerifiedAgeForAlcohol,
        initialPageLoad: state.initialPageLoad,
        isPortalAuthSession: state.isPortalAuthSession,
        isSignedIn: state.isSignedIn,
        locationId: state.locationId,
        orderDetails: state.orderDetails,
        orderExpirationTimer: state.orderExpirationTimer,
        orderId: state.orderId,
        prefillAccountInfo: state.prefillAccountInfo,
        route: state.route,
        signInRedirectRoute: state.signInRedirectRoute,
        smsNumberForReceipt: state.smsNumberForReceipt,
        smsReceiptOptIn: state.smsReceiptOptIn,
        websocketUser: state.websocketUser
      };
    },

    websocketUser(state) {
      return state.websocketUser;
    },

    isTableTopDeviceOrdering(state) {
      const isTableTopDeviceOrdering = storage.local.get('isTableTopDevice') || state.route.isTableTopDevice;

      return isTableTopDeviceOrdering;
    },

    unreadInboxMessageCount(state) {
      return state.currentUser.unreadInboxMessageCount || 0;
    },

    /**
     * Returns the first house account associated with the signed-in account, or null.
     * Populated by the `fetchHouseAccounts` action which hits
     * GET /cardfree-api/v1/account/me/house-accounts.
     *
     * Shape per entry: { publicId, totalLimit, limitPerOrder, isTaxExempt }.
     * HOUSE_ACCOUNT_TODO: outstandingBalance is not yet in the response — once added,
     * extend the per-total threshold check in cf-alternative-payment-providers.
     */
    houseAccount(state) {
      return state.currentUser.houseAccounts[0] || null;
    },

    isPortalAuthSession(state) {
      return state.isPortalAuthSession;
    }
  },

  actions: {
    async createAccount({ dispatch }, details) {
      const result = {
        error: null
      };

      try {
        const profile = configureProfileInfo(details);

        await http.post('/cardfree-api/v1/account', profile);

        const { error: signInError } = await dispatch('signIn', {
          email: details.email,
          password: details.password
        });

        if (signInError) Object.assign(result, { error: signInError });
      }

      catch (error) {
        Object.assign(result, { error });
      }

      return result;
    },

    async signInViaSSO(context, payload) {
      try {
        const httpBody = {
          grant_type: 'loyalty_sso',
          scope: 'passwordless',
          phone_number_or_email: payload.contactInfo
        };

        await http.post('/auth-api/sign-in-via-sso', httpBody);
      }
      catch (error) {
        throw error;
      }
    },

    async verifySSO({ dispatch }, payload) {
      try {
        const httpBody = {
          grant_type: 'loyalty_sso',
          scope: 'passwordless',
          authorization_code: payload.authCode
        };

        await http.post('/auth-api/verify-sso', httpBody);

        dispatch('signInSuccess');
      }
      catch (error) {
        errorHandlers.generic(error);
      }
    },

    async signInViaPortalAuthorization({ commit, dispatch }, payload) {
      try {
        const httpBody = {
          grant_type: 'authorization_code',
          scope: 'passwordless',
          authorization_code: payload.authCode
        };

        await http.post('/auth-api/sign-in-via-portal-auth', httpBody);

        commit('SET_IS_PORTAL_AUTH_SESSION', true);

        await dispatch('signInSuccess');

        return { isSuccess: true };
      }
      catch (error) {
        errorHandlers.generic(error);

        return { isSuccess: false };
      }
    },

    async enrollInLoyalty({ dispatch }) {
      try {
        await http.put('/cardfree-api/v1/account/me/loyaltyprogram');
        await dispatch('refreshProfile');
        await dispatch('offers/getOffers', {
          silent: true,
          silentError: true
        }, { root: true });
      }

      catch (error) {
        throw error;
      }
    },

    initialize({ commit, dispatch }) {
      const applicationFlow = storage.local.get('applicationFlow');
      const emailForReceipt = storage.session.get('emailForReceipt');
      const hasBeenPromptedToEnrollInLoyalty = storage.local.get('hasBeenPromptedToEnrollInLoyalty');
      const hasVerifiedAgeForAlcohol = storage.local.get('hasVerifiedAgeForAlcohol');
      const isPortalAuthSession = storage.local.get('isPortalAuthSession');
      const isSignedIn = storage.cookie.get('isSignedIn');
      const locationId = storage.local.get('locationId');
      const orderDetails = storage.local.get('orderDetails');
      const orderId = storage.local.get('orderId');
      const smsNumberForReceipt = storage.session.get('smsNumberForReceipt');
      const websocketUser = storage.local.get('websocketUser') || { id: uuid() };
      commit('SET_APPLICATION_FLOW', applicationFlow);
      commit('SET_IS_PORTAL_AUTH_SESSION', !!isPortalAuthSession);
      commit('SET_IS_SIGNED_IN', !!isSignedIn);

      dispatch('updateHasBeenPromptedToEnrollInLoyalty', hasBeenPromptedToEnrollInLoyalty);
      dispatch('updateHasVerifiedAgeForAlcohol', hasVerifiedAgeForAlcohol);
      dispatch('updateEmailForReceipt', emailForReceipt);
      dispatch('updateLocationId', locationId);
      dispatch('updateOrderDetails', orderDetails);
      dispatch('updateOrderId', orderId);
      dispatch('updateSmsNumberForReceipt', smsNumberForReceipt);
      dispatch('updateWebsocketUser', websocketUser);
      dispatch('content/initialize', null, { root: true });
    },

    async load({ commit, dispatch, state }) {
      try {
        if (!state.isSignedIn || state.currentUser.isLoading || state.currentUser.accountId) {
          return;
        }

        commit('SET_CURRENT_USER_LOAD_STATUS', true);

        await dispatch('loadCurrentUser', {
          skipFavorites: true
        });
      }

      catch (error) {
        errorHandlers.silent(error);

        await dispatch('signOut');
      }

      finally {
        commit('SET_CURRENT_USER_LOAD_STATUS', false);
      }
    },

    async loadCurrentUser({ commit, dispatch, rootGetters }, { autoEnrollLoyalty, skipFavorites } = {}) {
      const merchant = rootGetters['merchant/merchant'];

      let response = await http.get('/cardfree-api/v1/account/me', {
        params: {
          includeLoyaltyMember: merchant.options.loyaltyOptions.loyaltyEnabled
        }
      });

      if (autoEnrollLoyalty && !response.data.isEnrolledInLoyalty) {
        await http.put('/cardfree-api/v1/account/me/loyaltyprogram');

        response = await http.get('/cardfree-api/v1/account/me', {
          params: {
            includeLoyaltyMember: true
          }
        });
      }

      dispatch('getUsersFavoriteStore');

      commit('SET_IS_SIGNED_IN', true);

      commit('SET_CURRENT_USER', response.data);

      await dispatch('offers/getOffers', {
        loyaltyMember: response.data.loyaltyMember,
        silentError: true
      }, { root: true });

      if (!skipFavorites) {
        await dispatch('favoriteItems/getFavoriteItems', {
          silentError: true
        }, { root: true });
      }
    },

    async fetchHouseAccounts({ commit }) {
      try {
        const response = await http.get('/cardfree-api/v1/account/me/house-accounts');
        commit('SET_HOUSE_ACCOUNTS', response.data || []);
      }
      catch (error) {
        errorHandlers.silent(error);
        commit('SET_HOUSE_ACCOUNTS', []);
      }
    },

    handleExpiredOrder() {
      const expiredEvent = {
        customMessage: {
          title: 'Your order has expired.',
          description: 'You can start a new order by adding items to your cart.'
        }
      };

      window.setTimeout(() => {
        // NOTE: This small timeout ensures that layout.vue has been initiated
        events.emit('ordering-error', expiredEvent);
      }, 25);
    },

    async refreshProfile({ commit, state, rootGetters }) {
      if (!state.currentUser.accountId || state.currentUser.isRefreshing) {
        return;
      }

      try {
        commit('SET_REFRESH_PROFILE_LOAD_STATUS', true);
        const merchant = rootGetters['merchant/merchant'];

        const response = await http.get('/cardfree-api/v1/account/me', {
          params: {
            includeLoyaltyMember: merchant.options.loyaltyOptions.loyaltyEnabled
          }
        });
        commit('SET_CURRENT_USER', response.data);
      }

      catch (error) {
        errorHandlers.silent(error);
      }

      finally {
        commit('SET_REFRESH_PROFILE_LOAD_STATUS', false);
      }
    },

    async resendVerificationEmail({ state }) {
      try {
        await http.put('/cardfree-api/v1/account/email-verification/resend', {
          emailAddress: state.currentUser.email
        });

        events.emit('notification', {
          type: 'success',
          message: `Verification email sent to: ${state.currentUser.email}`,
          clickToDismiss: true
        });
      }

      catch (error) {
        errorHandlers.generic(error);
      }
    },

    respectOrderExpiration({ commit, dispatch, state }) {
      const timestamp = state.orderDetails.expirationTime;

      window.clearTimeout(state.orderExpirationTimer);

      if (!timestamp) {
        return;
      }

      const expiration = new Date(state.orderDetails.expirationTime).getTime();
      const now = Date.now();
      const millisecondsUntilExpiration = expiration - now;
      const hasExpired = millisecondsUntilExpiration < 0;

      if (hasExpired) {
        dispatch('handleExpiredOrder');
      }

      else {
        const timer = window.setTimeout(() => {
          dispatch('handleExpiredOrder');
        }, millisecondsUntilExpiration);

        commit('SET_ORDER_EXPIRATION_TIMER', timer);
      }
    },

    async requestAccountDeletion() {
      try {
        await http.post('/cardfree-api/v1/account/me/request-account-deletion');
        events.emit('notification', {
          type: 'success',
          message: 'Delete account request has been sent',
          duration: 5000
        });
      }
      catch (error) {
        errorHandlers.generic(error);
      }
    },

    async returnActualPickupTime({ getters, rootGetters }) {
      const { orderDetails } = getters;

      const merchant = rootGetters['merchant/merchant'];

      const order = rootGetters['orders/order'];

      const selectedLocation = rootGetters['locations/selectedLocation'];

      try {
        if (orderDetails.fulfillmentType === 'delivery') {
          return orderDetails.deliveryEstimate.pickupTime;
        }

        if (orderDetails.fulfillmentType === 'dine-in') {
          const pickupTimesConfig = {
            location: selectedLocation,
            menuType: 'DineIn',
            merchant,
            order
          };

          const asapTimestammp = await pickupTimes.getAsapTimestamp(pickupTimesConfig);

          return asapTimestammp;
        }

        return orderDetails.fulfillmentTime;
      }

      catch (error) {
        errorHandlers.silent(error);

        return null;
      }
    },

    setPrefillAccountInfo({ commit }, info) {
      commit('SET_PREFILL_ACCOUNT_INFO', info);
    },

    async signIn({ dispatch }, credentials) {
      const result = {
        error: null
      };

      try {
        const path = credentials.type
          ? '/auth-api/sign-in/social'
          : '/auth-api/sign-in';

        await http.post(path, credentials);

        await dispatch('signInSuccess');
      }

      catch (error) {
        Object.assign(result, { error });
      }

      return result;
    },

    async signInSuccess({ dispatch, rootState }, { autoEnrollLoyalty } = {}) {
      const args = ['loadCurrentUser'];

      const { claimCode } = rootState.offers.promoCode;

      if (autoEnrollLoyalty) args.push({ autoEnrollLoyalty });

      if (rootState.offers.giftingPoints.pointGiftClaimCode) {
        await dispatch('offers/claimGiftedLoyaltyPoints', null, { root: true });
      }

      await dispatch(...args);

      if (claimCode) {
        await dispatch('offers/claimPromoCode', null, { root: true });
      }

      await dispatch('orders/assignOrderToUser', null, { root: true });

      await dispatch('orders/getOrder', { silent: true }, { root: true });

      if (rootState.locations?.selectedLocation?.paymentGateway?.methods?.houseAccount) {
        dispatch('fetchHouseAccounts');
      }

      events.emit('signed-in');
    },

    async signOut({ commit, dispatch, getters, rootGetters, state }, options = {}) {
      const { orderDetails } = getters;
      const order = rootGetters['orders/order'];

      if (!state.isSignedIn) {
        return;
      }

      if (options.userInitiated) {
        if (orderDetails.orderType === 'group' && order.groupOrder.you.isHost) {
          dispatch('modals/openModal', {
            name: 'confirmation',
            data: {
              ctaConfirmText: 'Yes, Sign Out',
              primaryText: 'Warning! You currently have an open group order. Signing out will clear your cart and cancel your group order. Are you sure you’d like to proceed?',
              onConfirm: () => dispatch('signOut'),
              title: 'Your order will be lost!'
            }
          }, { root: true });

          return;
        }
      }

      if (!['table', 'pay'].includes(orderDetails.orderType)) {
        /*
          NOTE: We don't want to unset order or location details when in
          the P@T/O@T context.
        */

        dispatch('unsetLocation');
        await dispatch('unsetOrder');
        dispatch('updateOrderDetails', {
          fulfillmentType: 'pick-up',
          menuType: null,
          orderType: 'standard'
        });
      }

      commit('UNSET_CURRENT_USER');
      commit('SET_IS_SIGNED_IN', false);
      commit('favoriteItems/SET_FAVORITE_ITEMS', [], { root: true });
      commit('favoriteOrders/SET_FAVORITE_ORDERS', [], { root: true });
      commit('giftCards/UNSET', null, { root: true });
      commit('offers/SET_OFFERS', [], { root: true });
      commit('offers/SET_LOYALTY_SUMMARY', null, { root: true });
      commit('savedCards/SET_SAVED_CARDS', [], { root: true });

      try {
        await http.post('/auth-api/sign-out');
      }

      catch (error) {
        errorHandlers.silent(error);
      }

      finally {
        events.emit('signed-out');
      }
    },


    async signInViaDevice({ dispatch }, payload) {
      try {
        const body = { ...payload };

        if (payload.phoneNumber) {
          body.phoneNumber = payload.phoneNumber.replace(/[^0-9]/g, '');
        }

        await http.post('/auth-api/sign-in/device', body);

        await dispatch('signInSuccess', { autoEnrollLoyalty: true });
      }

      catch (error) {
        throw error;
      }
    },


    // eslint-disable-next-line no-unused-vars
    async requestTwoFactorAuthPasscode({ commit }, payload) {
      try {
        const body = formatAuthVerificationBody(payload);

        await http.post('/auth-api/device-verification/request', body);

        events.emit('notification', {
          type: 'success',
          message: 'A new passcode has been sent',
          duration: 5000
        });

        return { isSuccess: true };
      }

      catch (error) {
        errorHandlers.generic(error);

        return { isSuccess: false };
      }
    },

    // eslint-disable-next-line no-unused-vars
    async resendTwoFactorAuthPasscode({ commit }, payload) {
      try {
        const body = formatAuthVerificationBody(payload);

        await http.post('/auth-api/device-verification/resend', body);

        events.emit('notification', {
          type: 'success',
          message: 'A new passcode has been sent',
          duration: 5000
        });

        return { isSuccess: true };
      }

      catch (error) {
        errorHandlers.generic(error);

        return { isSuccess: false };
      }
    },


    async submitTwoFactorAuthPasscode({ dispatch }, payload) {
      try {
        const body = formatAuthVerificationBody(payload);

        await http.post('/auth-api/device-verification', body);

        await dispatch('signInSuccess');

        return { isSuccess: true };
      }
      catch (error) {
        errorHandlers.generic(error);

        return { isSuccess: false };
      }
    },

    unsetLocation({ commit, dispatch }) {
      dispatch('updateLocationId', null);

      commit('locations/UNSET_SELECTED_LOCATION', null, { root: true });
    },

    async unsetOrder({ commit, dispatch, getters, rootGetters }, {
      doNotResetRouteParams = false,
      doNotRefreshMenu = false,
      cancelled = false,
      skipMenuTypeChange = false
    } = {}) {
      const order = rootGetters['orders/order'];
      const selectedLocation = rootGetters['locations/selectedLocation'];

      const { orderDetails } = getters;
      let shouldCancelOrder = false;
      let shouldRefreshMenu = false;
      const updatedOrderDetails = {
        fulfillmentTime: null,
        expirationTime: null
      };

      if (!doNotResetRouteParams) {
        dispatch('updateRouteParamsDirectly', {
          orderId: null,
          tableNumber: null,
          ticketNumber: null
        });
      }

      if (!cancelled) {
        updatedOrderDetails.menuType = null;
      }

      if (orderDetails.orderType === 'group') {
        updatedOrderDetails.orderType = 'standard';

        dispatch('orders/syncGroupOrderIntervalStop', null, { root: true });

        if (order.groupOrder.you && !order.groupOrder.you.isHost) {
          updatedOrderDetails.fulfillmentType = 'pick-up';
          shouldRefreshMenu = true;
        }

        if (order.groupOrder.you && order.groupOrder.you.isHost) {
          shouldCancelOrder = true;
        }
      }

      else if (orderDetails.orderType === 'standard' && !cancelled) {
        if (orderDetails.fulfillmentType === 'dine-in') {
          const dineInValidation = selectedLocation.validations.find(validation => validation.fulfillmentType === 'DineIn');

          const timeValue = dineInValidation?.orderExpiration ?? 120;
          updatedOrderDetails.expirationTime = moment().utc().add(timeValue, 'minutes').format();

          updatedOrderDetails.fulfillmentType = 'dine-in';
          shouldRefreshMenu = true;
        }
      }

      if (shouldCancelOrder && order.orderState === 'Open') {
        try {
          await http.delete(`/cardfree-api/v1/account/me/order/${order.orderId}`);
        }
        catch (error) {
          errorHandlers.silent(error);
        }
      }

      /**
       * Temp hack to potentially fix an unreproducable bug.
       * https://cardfree.atlassian.net/browse/WAP-1481
       */
      if (skipMenuTypeChange) updatedOrderDetails.skipMenuTypeChange = true;

      dispatch('updateOrderId', null);

      storage.local.set('orderItemsStaged', null);

      commit('orders/UNSET_ORDER', null, { root: true });

      commit('giftCards/UNSET', null, { root: true });

      dispatch('orders/updateOrder', updatedOrderDetails, { root: true });

      if (shouldRefreshMenu && !doNotRefreshMenu) {
        dispatch('menu/getMenu', { silent: true }, { root: true });
      }
    },

    updateEmailForReceipt({ commit }, email) {
      storage.session.set('emailForReceipt', email);
      commit('SET_EMAIL_FOR_RECEIPT', email);
    },

    updateHasBeenPromptedToEnrollInLoyalty({ commit }, hasBeenPrompted) {
      storage.local.set('hasBeenPromptedToEnrollInLoyalty', hasBeenPrompted);
      commit('SET_HAS_BEEN_PROMPTED_TO_ENROLL_IN_LOYALTY', hasBeenPrompted);
    },

    updateHasVerifiedAgeForAlcohol({ commit }, hasVerified) {
      storage.local.set('hasVerifiedAgeForAlcohol', hasVerified);
      commit('SET_HAS_VERIFIED_AGE_FOR_ALCOHOL', hasVerified);
    },

    updateLocationId({ commit }, id) {
      const intId = Number(id) || null;
      storage.local.set('locationId', intId);
      commit('SET_LOCATION_ID', intId);
    },

    updateOrderDetails({ commit, dispatch }, details) {
      storage.local.set('orderDetails', details);

      commit('SET_ORDER_DETAILS', details || {});

      dispatch('respectOrderExpiration');
    },

    updateOrderId({ commit }, id) {
      storage.local.set('orderId', id);

      commit('SET_ORDER_ID', id);
    },

    async updateNotifications({ commit }, settings) {
      try {
        await http.put('/cardfree-api/v1/account/me/notifications', settings);
        const response = await http.get('/cardfree-api/v1/account/me');

        commit('SET_CURRENT_USER', response.data);
      }

      catch (error) {
        throw error;
      }
    },

    async updateProfile({ commit }, details) {
      try {
        if (Object.keys(details).length === 0) {
          return;
        }

        const profile = configureProfileInfo(details);

        await http.put('/cardfree-api/v1/account/me', profile);
        const response = await http.get('/cardfree-api/v1/account/me');

        commit('SET_CURRENT_USER', response.data);
      }

      catch (error) {
        throw error;
      }
    },

    async updateUsersFavoriteStore({ dispatch, state }, storeId) {
      try {
        const newFavoriteStore = { isFavorite: true, storeId };
        const oldFavoriteStore = state.currentUser.favoriteStore.storeId
          && { isFavorite: false, storeId: state.currentUser.favoriteStore.storeId };

        if (oldFavoriteStore) await http.post('/cardfree-api/v1/account/me/favorite-store-updates', [newFavoriteStore, oldFavoriteStore]);
        else await http.post('/cardfree-api/v1/account/me/favorite-stores', [newFavoriteStore]);

        await dispatch('getUsersFavoriteStore');
      }
      catch (error) {
        errorHandlers.silent(error);
      }
    },

    async getUsersFavoriteStore({ commit }) {
      try {
        const { data } = await http.get('/cardfree-api/v1/account/me/favorite-stores');
        const [firstStore] = data;

        if (firstStore) commit('SET_FAVORITE_STORE_DATA', firstStore);
      }
      catch (error) {
        errorHandlers.silent(error);
      }
    },

    captureCampaignId({ commit, state }, { route }) {
      const id = route?.query?.campaignId;

      if (id) {
        storage.local.set('campaignId', id);
        commit('SET_CAMPAIGN_ID', id);
        return;
      }

      if (state.campaignId == null) {
        const stored = storage.local.get('campaignId');
        if (stored) commit('SET_CAMPAIGN_ID', stored);
      }
    },

    clearCampaignId({ commit }) {
      storage.local.remove('campaignId');
      commit('SET_CAMPAIGN_ID', null);
    },

    updateRouteParams({ commit }, options) {
      let route = {};
      const { params, query } = options.route;

      if (!options.override) {
        route = storage.session.get('route') || {};
      }

      const cancelUrl = query.cu;
      const channelTypeId = query.ch;
      const customQuery = query.cq;
      const disableSplit = query.disableSplit || query.ds;
      const enrollInLoyalty = 'enrollInLoyalty' in query ? '1' : undefined;
      const { merchantId } = params;
      const merchantLocationId = query.merchantLocationId || query.ml;
      const orderId = query.orderId || query.oi;
      const orderGuid = query.orderGuid || query.og;
      const orderType = query.orderType || query.ot;
      const partnerIdentification = query.pi;
      const posLocationId = query.posLocationId || query.pl;
      const returnUrl = query.returnUrl || query.ru;
      const storeId = query.storeId || query.si;
      const sublocationToken = query.sublocationToken || query.st;
      const successUrl = query.su;
      const tableNumber = query.tableNumber || query.tb;
      const ticketNumber = query.ticketNumber || query.tn;
      const userLocationType = query.lt;
      const utmSource = query.utm_source;
      const isTableTopDevice = !!query.ttd;

      // eslint-disable-next-line prefer-destructuring
      const action = query.action;

      route.cancelUrl = cancelUrl || route.cancelUrl;
      route.channelTypeId = channelTypeId || route.channelTypeId;
      route.customQuery = customQuery || route.customQuery;
      route.disableSplit = disableSplit || route.disableSplit;
      route.enrollInLoyalty = enrollInLoyalty || route.enrollInLoyalty;
      route.merchantId = merchantId || route.merchantId;
      route.merchantLocationId = merchantLocationId || route.merchantLocationId;
      route.orderId = orderId || route.orderId;
      route.orderGuid = orderGuid || route.orderGuid;
      route.orderType = orderType || route.orderType;
      route.partnerIdentification = partnerIdentification || route.partnerIdentification;
      route.posLocationId = posLocationId || route.posLocationId;
      route.returnUrl = returnUrl || route.returnUrl;
      route.storeId = storeId || route.storeId;
      route.sublocationToken = sublocationToken || route.sublocationToken;
      route.successUrl = successUrl || route.successUrl;
      route.tableNumber = tableNumber || route.tableNumber;
      route.ticketNumber = ticketNumber || route.ticketNumber;
      route.userLocationType = userLocationType || route.userLocationType;
      route.utmSource = utmSource || route.utmSource;
      route.action = action || route.action;
      route.isTableTopDevice = isTableTopDevice || route.isTableTopDevice;

      route.cancelUrl = route.cancelUrl || route.successUrl;

      storage.session.set('route', route);

      commit('SET_ROUTE_PARAMS', route);
    },

    updateRouteParamsDirectly({ commit }, params) {
      const existingRoute = storage.session.get('route') || {};

      const route = { ...existingRoute, ...params };

      storage.session.set('route', route);

      commit('SET_ROUTE_PARAMS', route);
    },

    updateSmsNumberForReceipt({ commit }, number) {
      storage.session.set('smsNumberForReceipt', number);
      commit('SET_SMS_NUMBER_FOR_RECEIPT', number);
    },

    updateSmsReceiptOptIn({ commit }, optIn) {
      commit('SET_SMS_RECEIPT_OPT_IN', optIn);
    },

    updateWebsocketUser({ commit, state }, websocketUser) {
      const user = {
        id: websocketUser.id || state.websocketUser.id,
        nickname: websocketUser.nickname
      };

      storage.local.set('websocketUser', user);

      commit('SET_WEBSOCKET_USER', user);
    },

    setIsTableTopDevice() {
      storage.local.set('isTableTopDevice', true);
    },

    updateUnreadInboxMessageCount({ commit }, count) {
      commit('SET_UNREAD_INBOX_MESSAGE_COUNT', count);
    },

    decrementUnreadInboxMessageCount({ commit, state }) {
      const newCount = Math.max(0, state.currentUser.unreadInboxMessageCount - 1);
      commit('SET_UNREAD_INBOX_MESSAGE_COUNT', newCount);
    },

    async loadInboxMessages(context, { currentPage = 0, pageSize = 10 } = {}) {
      try {
        const response = await http.get('/cardfree-api/v1/inbox/messages', {
          params: {
            currentPage,
            pageSize
          }
        });

        const { data, paging } = response.data;

        return { data, paging };
      }
      catch (error) {
        errorHandlers.silent(error);
        throw error;
      }
    },

    async markInboxMessageAsRead({ dispatch }, messageId) {
      try {
        await http.patch(`/cardfree-api/v1/inbox/messages/${messageId}`, {
          isRead: true
        });

        dispatch('decrementUnreadInboxMessageCount');
      }
      catch (error) {
        errorHandlers.silent(error);
        throw error;
      }
    },

    async deleteInboxMessage(context, messageId) {
      try {
        await http.patch(`/cardfree-api/v1/inbox/messages/${messageId}`, {
          isDeleted: true
        });

        // Note: We don't decrement here as the inbox page will handle this
        // based on whether the deleted message was unread
      }
      catch (error) {
        errorHandlers.silent(error);
        throw error;
      }
    },

    setApplicationFlowFromRoute({ commit, state }, routeParentNamespace) {
      let applicationFlow;
      switch (routeParentNamespace) {
        case 'order':
          applicationFlow = applicationFlows.ORDER_AHEAD;
          break;
        case 'orderAtTable':
          applicationFlow = applicationFlows.ORDER_AT_TABLE;
          break;
        case 'payAtTable':
          applicationFlow = applicationFlows.PAY_AT_TABLE;
          break;
        default:
          // Don't override existing application flow for merchant-level routes
          // Only clear it if we have no existing flow
          applicationFlow = state.applicationFlow || null;
          break;
      }

      commit('SET_APPLICATION_FLOW', applicationFlow);
    },

    clearApplicationFlow({ commit }) {
      commit('SET_APPLICATION_FLOW', null);
    }
  },

  mutations: {
    SET_CAMPAIGN_ID(state, id) {
      state.campaignId = id;
    },

    SET_CURRENT_USER(state, user) {
      state.currentUser.accountId = user.accountId;
      state.currentUser.accountStatus = user.accountStatus;
      state.currentUser.allowInStoreOfferRedemption = user.allowInStoreOfferRedemption;
      state.currentUser.allowOfferRedemption = user.allowOfferRedemption;
      state.currentUser.birthdayInfo = user.birthdayInfo;
      state.currentUser.company = user.company;
      state.currentUser.email = user.email;
      state.currentUser.firstName = user.firstName;
      state.currentUser.isEnrolledInLoyalty = user.isEnrolledInLoyalty;
      state.currentUser.lastName = user.lastName;
      state.currentUser.notificationPreferences = user.notificationPreferences || {};
      state.currentUser.primarySmsNumber = user.primarySmsNumber;
      state.currentUser.usesThirdPartyAuthentication = user.usesThirdPartyAuthentication;
      state.currentUser.zip = user.zip;
      state.currentUser.emailVerified = user.emailVerified;
      state.currentUser.primarySmsNumberVerified = user.primarySmsNumberVerified;
      state.currentUser.emailVerificationRequired = user.emailVerificationRequired;
      state.currentUser.phoneNumberVerificationRequired = user.phoneNumberVerificationRequired;
      state.currentUser.unreadInboxMessageCount = user.unreadInboxMessageCount || 0;
    },

    SET_CURRENT_USER_LOAD_STATUS(state, isLoading) {
      state.currentUser.isLoading = isLoading;
    },

    SET_EMAIL_FOR_RECEIPT(state, email) {
      state.emailForReceipt = email;
    },

    SET_INITIAL_PAGE_LOAD(state, initialPageLoad) {
      state.initialPageLoad = initialPageLoad;
    },

    SET_IS_PORTAL_AUTH_SESSION(state, isPortalAuthSession) {
      state.isPortalAuthSession = !!isPortalAuthSession;

      if (state.isPortalAuthSession) {
        storage.local.set('isPortalAuthSession', true);
      }

      else {
        storage.local.remove('isPortalAuthSession');
      }
    },

    SET_IS_SIGNED_IN(state, isSignedIn) {
      state.isSignedIn = isSignedIn;
    },

    SET_HAS_BEEN_PROMPTED_TO_ENROLL_IN_LOYALTY(state, hasBeenPrompted) {
      state.hasBeenPromptedToEnrollInLoyalty = hasBeenPrompted;
    },

    SET_HAS_VERIFIED_AGE_FOR_ALCOHOL(state, hasVerified) {
      state.hasVerifiedAgeForAlcohol = hasVerified;
    },

    SET_LOCATION_ID(state, id) {
      state.locationId = id;
    },

    SET_HOUSE_ACCOUNTS(state, houseAccounts) {
      state.currentUser.houseAccounts = Array.isArray(houseAccounts) ? houseAccounts : [];
    },

    SET_FAVORITE_STORE_DATA(state, store) {
      state.currentUser.favoriteStore = {
        storeId: store.storeId,
        description: store.description,
        businessLocationId: store.businessLocationId,
        isFavorite: store.isFavorite
      };
    },

    SET_ORDER_DETAILS(state, details) {
      state.orderDetails.deliveryAddress = details.deliveryAddress;
      state.orderDetails.deliveryEstimate = details.deliveryEstimate;
      state.orderDetails.deliveryAddressGeoCoordinates = details.deliveryAddressGeoCoordinates;
      state.orderDetails.expirationTime = details.expirationTime;
      state.orderDetails.fulfillmentTime = details.fulfillmentTime;
      state.orderDetails.fulfillmentType = details.fulfillmentType;
      state.orderDetails.pickupType = details.pickupType;
      state.orderDetails.groupMemberGuestId = details.groupMemberGuestId;
      state.orderDetails.menuType = details.menuType;
      state.orderDetails.orderType = details.orderType;
      state.orderDetails.tableNumber = details.tableNumber;
    },

    SET_ORDER_EXPIRATION_TIMER(state, timer) {
      state.orderExpirationTimer = timer;
    },

    SET_ORDER_ID(state, id) {
      state.orderId = id;
    },

    SET_PREFILL_ACCOUNT_INFO(state, info) {
      state.prefillAccountInfo = info;
    },

    SET_REFRESH_PROFILE_LOAD_STATUS(state, status) {
      state.currentUser.isRefreshing = status;
    },

    SET_ROUTE_PARAMS(state, params) {
      params.channelTypeId = params.channelTypeId ? Number(params.channelTypeId) : 0;
      params.disableSplit = params.disableSplit === '1';
      params.enrollInLoyalty = params.enrollInLoyalty === '1';

      state.route = params;
    },

    SET_SIGN_IN_REDIRECT_ROUTE(state, route) {
      state.signInRedirectRoute = route;
    },

    SET_SMS_NUMBER_FOR_RECEIPT(state, number) {
      state.smsNumberForReceipt = number;
    },

    SET_SMS_RECEIPT_OPT_IN(state, optIn) {
      state.smsReceiptOptIn = optIn;
    },

    SET_WEBSOCKET_USER(state, user) {
      state.websocketUser.id = user.id;
      state.websocketUser.nickname = user.nickname;
    },

    UNSET_CURRENT_USER(state) {
      state.currentUser.accountId = null;
      state.currentUser.accountStatus = null;
      state.currentUser.birthdayInfo = null;
      state.currentUser.company = null;
      state.currentUser.email = null;
      state.currentUser.firstName = null;
      state.currentUser.isEnrolledInLoyalty = false;
      state.currentUser.lastName = null;
      state.currentUser.notificationPreferences = {
        email: false,
        marketingOptIn: false,
        sms: false
      };
      state.currentUser.primarySmsNumber = null;
      state.currentUser.signInRedirectRoute = null;
      state.currentUser.usesThirdPartyAuthentication = false;
      state.currentUser.zip = null;
      state.currentUser.favoriteStore = {
        businessLocationId: null,
        description: null,
        isFavorite: null,
        storeId: null
      };
      state.currentUser.houseAccounts = [];
      state.currentUser.unreadInboxMessageCount = 0;
      state.isPortalAuthSession = false;
      storage.local.remove('isPortalAuthSession');
    },

    SET_UNREAD_INBOX_MESSAGE_COUNT(state, count) {
      state.currentUser.unreadInboxMessageCount = count || 0;
    },

    SET_APPLICATION_FLOW(state, applicationFlow) {
      state.applicationFlow = applicationFlow;

      // Save to localStorage to persist across page refreshes
      if (applicationFlow) {
        storage.local.set('applicationFlow', applicationFlow);
      }

      else {
        storage.local.remove('applicationFlow');
      }
    }
  }
};
